// COMMS-HUB-B2 (2026-08-29) — starting a text or a WhatsApp from the verb.
//
// Mark, choosing layout B2: "is the dialler etc incorporated if so can we have
// phone symbol and whatsapp icon and text icon to compose etc?"
//
// Three buttons beat the single "New message" one this replaced. People don't
// think "I want to communicate with Guest Building, by phone" — they think
// "call Guest Building". Starting from the VERB means the channel is already
// decided and the picker has one job.
//
// This sheet owns two of those three verbs:
//
//   sms       → pick someone, type, send.
//   whatsapp  → same, on the other channel.
//
// **Call is not here.** It was, briefly, as a contact list that dialled on
// selection — and Mark's verdict was immediate: *"in the comms panel call
// should use the full dialler pop up not the condensed version currently."*
// He was right; it was a downgrade from a panel that already existed. Call now
// opens `DiallerModal`, which wraps the real `DiallerPanel`. See its header.
//
// The Dialler's other three tabs still collapse into this sheet: Contacts is
// this picker, Keypad is the toggle below (a number read off a letter is as
// likely to need a text as a call), and Recent was never anything but the
// conversation list built twice. To file became a chip on that list.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import axios from "axios";
import { toast } from "sonner";
import {
  Search, Send, Loader2, X, Hash, MessageSquare, Delete,
} from "lucide-react";

import { API } from "@/components/admin/email/emailConstants";

// COMMS-HUB-B2c — `call` was here and is not any more. The Call button opens
// the real DiallerPanel in a modal instead (DiallerModal), because a contact
// list plus a keypad toggle was a downgrade from the panel that already
// existed. Two ways to place a call would drift; there is one.
const MODE = {
  sms:      { label: "SMS",      verb: "Text",     Icon: MessageSquare },
  whatsapp: { label: "WhatsApp", verb: "WhatsApp", Icon: MessageSquare },
};

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

/** Loose AU/E.164 normalisation, mirroring PhoneLink and services/phone.py.
 *  Conservative on purpose: null rather than a guess, because a wrong number
 *  here sends a client's business to a stranger — or rings them. */
function toE164(raw) {
  const text = String(raw || "").trim();
  if (!text) return null;
  const digits = text.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return /^\+\d{8,15}$/.test(digits) ? digits : null;
  const bare = digits.replace(/\D/g, "");
  if (bare.startsWith("0") && bare.length === 10) return `+61${bare.slice(1)}`;
  if (bare.startsWith("61") && bare.length >= 10) return `+${bare}`;
  return null;
}

/**
 * @param prefill  { number, name } — A3 (2026-08-30). Opened from a number
 *   somewhere else in the CRM (a client record, a lead row), where the
 *   recipient is already known and the contact search is a step backwards:
 *   you have just clicked the person's number, so being asked to find them is
 *   asking a question you have already answered.
 */
export default function ComposeSheet({
  open, mode = "sms", onClose, canSend = [], onSent, prefill = null,
}) {
  const [query, setQuery] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keypad, setKeypad] = useState(false);
  const [picked, setPicked] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const searchRef = useRef(null);
  const bodyRef = useRef(null);

  const cfg = MODE[mode] || MODE.sms;

  useEffect(() => {
    if (!open) return undefined;
    // Full reset on open. A half-typed message to the previous recipient
    // reappearing is how the wrong person gets a message.
    setQuery(""); setDraft(""); setKeypad(false);
    // A3 — straight to the message when we already know who. The recipient is
    // still SHOWN and still removable, because "who is this going to" must be
    // answerable at a glance no matter which door you came in through.
    const seeded = prefill?.number
      ? { number: prefill.number, name: prefill.name || prefill.number }
      : null;
    setPicked(seeded);
    const t = setTimeout(
      () => (seeded ? bodyRef.current : searchRef.current)?.focus(), 50);
    return () => clearTimeout(t);
  }, [open, mode, prefill?.number, prefill?.name]);

  useEffect(() => {
    if (!open || picked || keypad) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/comms/contacts`, {
          params: { q: query.trim() || undefined, limit: 40 },
        });
        if (!cancelled) setContacts(res.data?.contacts || []);
      } catch {
        if (!cancelled) setContacts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, query, picked, keypad]);

  const typedNumber = useMemo(() => toE164(query), [query]);

  const choose = useCallback((contact) => {
    setPicked(contact);
    setTimeout(() => bodyRef.current?.focus(), 50);
  }, []);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || !picked?.number) return;
    setSending(true);
    try {
      await axios.post(`${API}/comms/message`, {
        to: picked.number, body: text, channel: mode,
      });
      toast.success(`${cfg.label} sent to ${picked.name}`);
      onSent?.(picked.number);
      onClose?.();
    } catch (err) {
      // The server owns the 24-hour window and the "your number can't send
      // WhatsApp" case, and its wording says what to do instead.
      toast.error(err?.response?.data?.detail || "Couldn't send that message.");
    } finally {
      setSending(false);
    }
  }, [draft, picked, mode, cfg.label, onClose, onSent]);

  if (!open) return null;

  const unavailable = !canSend.includes(mode);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
         data-testid="compose-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-xl
                      shadow-xl max-h-[85dvh] flex flex-col">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 shrink-0">
          <cfg.Icon className="w-4 h-4 text-[#bd9a59]" />
          <h2 className="text-sm font-semibold text-[#021d49] flex-1">
            New {cfg.label}
          </h2>
          <button type="button" onClick={onClose}
                  className="p-1 rounded-md text-gray-400 hover:bg-gray-50"
                  aria-label="Close" data-testid="compose-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {unavailable ? (
          <div className="p-4">
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              None of your Solara numbers can send {cfg.label}. An admin can
              tick it on your number in Systems → Comms numbers.
            </p>
          </div>
        ) : !picked ? (
          <>
            <div className="p-3 shrink-0 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setKeypad(false); }}
                  placeholder={`${cfg.verb} a client, lead, or number`}
                  className="w-full text-sm bg-white border border-gray-200 rounded-md pl-8 pr-2 py-2
                             focus:outline-none focus:ring-1 focus:ring-[#bd9a59]"
                  data-testid="compose-search"
                />
              </div>
              <button
                type="button"
                onClick={() => setKeypad((k) => !k)}
                className={`h-9 w-9 rounded-md border flex items-center justify-center shrink-0 ${
                  keypad ? "border-[#021d49] bg-[#021d49] text-white"
                         : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
                aria-label="Keypad"
                data-testid="compose-keypad-toggle"
              >
                <Hash className="w-4 h-4" />
              </button>
            </div>

            {keypad ? (
              // The Dialler's keypad, in the one place you now start anything.
              <div className="p-3 pt-0 flex flex-col gap-2">
                <div className="text-center text-lg tabular-nums text-[#021d49] py-2 min-h-[2.5rem]">
                  {query || <span className="text-gray-300">Enter a number</span>}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {KEYS.map((k) => (
                    <button
                      key={k} type="button"
                      onClick={() => setQuery((q) => q + k)}
                      className="py-3 rounded-md bg-gray-50 hover:bg-gray-100 text-[#021d49] text-lg tabular-nums"
                      data-testid={`compose-key-${k}`}
                    >{k}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setQuery((q) => q.slice(0, -1))}
                          className="h-10 px-3 rounded-md border border-gray-200 text-gray-500"
                          aria-label="Delete" data-testid="compose-key-del">
                    <Delete className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    disabled={!typedNumber}
                    onClick={() => choose({ name: typedNumber, number: typedNumber, kind: "number" })}
                    className="flex-1 h-10 rounded-md bg-[#021d49] text-white text-sm
                               inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                    data-testid="compose-keypad-go"
                  >
                    <cfg.Icon className="w-3.5 h-3.5" />
                    {cfg.verb} {typedNumber || ""}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto px-1 pb-2">
                {typedNumber && (
                  <button
                    type="button"
                    onClick={() => choose({ name: typedNumber, number: typedNumber, kind: "number" })}
                    className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center gap-2.5"
                    data-testid="compose-raw-number"
                  >
                    <Hash className="w-4 h-4 text-[#bd9a59] shrink-0" />
                    <span className="text-sm text-[#021d49]">{cfg.verb} {typedNumber}</span>
                    <span className="text-[11px] text-gray-400 ml-auto">not in the CRM</span>
                  </button>
                )}

                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-4 h-4 animate-spin text-[#bd9a59]" />
                  </div>
                ) : contacts.length === 0 && !typedNumber ? (
                  <div className="text-center py-8 px-4">
                    <p className="text-sm text-gray-500">
                      {query ? "Nobody matches that." : "No contacts with a number on file."}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      You can type a number, or use the keypad.
                    </p>
                  </div>
                ) : contacts.map((c) => (
                  <button
                    key={`${c.kind}-${c.id}`}
                    type="button"
                    onClick={() => choose(c)}
                    className="w-full text-left px-3 py-2.5 hover:bg-gray-50"
                    data-testid={`compose-contact-${c.id}`}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-[#021d49] truncate flex-1">{c.name}</span>
                      <span className="text-[10px] text-gray-400 shrink-0 tabular-nums">{c.number}</span>
                    </div>
                    {c.subtitle && (
                      <p className="text-[11px] text-gray-500 truncate">{c.subtitle}</p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="p-3 flex flex-col gap-3 min-h-0">
            <div className="flex items-center gap-2 bg-gray-50 rounded-md px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[#021d49] truncate">{picked.name}</p>
                <p className="text-[11px] text-gray-500 tabular-nums">{picked.number}</p>
              </div>
              <button type="button" onClick={() => setPicked(null)}
                      className="text-[11px] text-gray-500 hover:text-[#021d49] shrink-0"
                      data-testid="compose-change-recipient">
                Change
              </button>
            </div>

            <textarea
              ref={bodyRef}
              rows={4}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
              }}
              placeholder={`${cfg.label} to ${picked.name}…`}
              className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2
                         focus:outline-none focus:ring-1 focus:ring-[#bd9a59]"
              data-testid="compose-body"
            />

            {/* No 24-hour warning here, deliberately. A brand-new WhatsApp
                conversation is ALWAYS outside the window — the client has
                never written — so the server refuses it and says why. A
                warning on every compose would train people to ignore the one
                in the reply box, where the window genuinely opens and closes. */}

            <div className="flex items-center gap-2">
              {/* COMMS-SEND-1 (2026-09-11) — no sender picker: comms-api
                  resolves who this goes out from itself (the broker's own
                  default number for SMS, the tenant's one number for
                  WhatsApp), with no caller-ID-style override. */}
              <p className="text-[10px] text-gray-400 flex-1">⌘↵ to send</p>
              <button
                type="button"
                onClick={send}
                disabled={sending || !draft.trim()}
                className="h-9 px-4 rounded-md bg-[#021d49] text-white text-sm
                           inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="compose-send"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                         : <Send className="w-3.5 h-3.5" />}
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
